import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import * as PermissionConstants from './context/PermissionConstants'
import './App.css';
import LoginPage from './components/auth/LoginPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoadingSpinner from './components/common/LoadingSpinner';
import { ACCOUNT_LEGACY_REDIRECTS, CREATE_INVOICE_MAX_RANK } from './components/invoices/accountsNavigation';

// Route-level code splitting: every page below is fetched on demand, so a
// cold load ships the shell and the matched route rather than all ~60 pages.
// The one <Suspense> boundary around <Routes> renders the fallback while the
// matched chunk is in flight.
const DashboardPage = lazy(() => import('./components/dashboard/DashboardPage'));
const CreateHorse = lazy(() => import('./components/horses/CreateHorse'));
const MovementsPage = lazy(() => import('./components/movements/MovementsPage2'));
const MedicalPage = lazy(() => import('./components/medical/MedicalPage2'));
const OverviewPage = lazy(() => import('./components/overview/OverviewPage2'));
const UnauthorizedPage = lazy(() => import('./components/auth/UnauthorizedPage'));
const EditHorsePage = lazy(() => import('./components/horses/EditHorsePage'));
const BillingWizardPage = lazy(() => import('./components/horses/BillingWizardPage'));
const MyClientsPage = lazy(() => import('./components/clients/MyClientsPage'));
const SecurityTest = lazy(() => import('./components/common/SecurityTest'));
const AppointmentPage = lazy(() => import('./components/appointments/AppointmentPage2'));
const MyAppointmentsPage = lazy(() => import('./components/appointments/MyAppointmentsPage'));
const VetBoardPage = lazy(() => import('./components/vet/VetBoardPage'));
const WorkflowPage = lazy(() => import('./components/overview/WorkflowPage2'));
const FarmMapPage = lazy(() => import('./components/farm/FarmMapPage'));
const HorseMediaPage = lazy(() => import('./components/horses/HorseMediaPage'));
const ReportsPage = lazy(() => import('./components/reports/ReportsPage'));
const FeedPage = lazy(() => import('./components/feed/FeedPage'));
const MediaPage = lazy(() => import('./components/media/MediaPage'));
const TimesheetPage = lazy(() => import('./components/timesheets/TimesheetPage'));
const FarmWorkPage = lazy(() => import('./components/farm/FarmWorkPage'));
const Timesheets = lazy(() => import('./components/timesheets/Timesheets'));
const MediaUploadPage = lazy(() => import('./components/media/MediaUploadPage'));
const DrugUsagePage = lazy(() => import('./components/medical/DrugUsagePage'));
const MyMedicationsPage = lazy(() => import('./components/medical/MyMedicationsPage'));
const InvoicesPage = lazy(() => import('./components/invoices/InvoicesPage'));
const InvoiceInspectionPage = lazy(() => import('./components/invoices/InvoiceInspectionPage'));
const InvoiceIncomePage = lazy(() => import('./components/invoices/InvoiceIncomePage'));
const IncomingAnalysisPage = lazy(() => import('./components/invoices/IncomingAnalysisPage'));
const FinanceSummaryPage = lazy(() => import('./components/invoices/FinanceSummaryPage'));
const HorseCostsPage = lazy(() => import('./components/invoices/HorseCostsPage'));
const AccountsHubPage = lazy(() => import('./components/invoices/AccountsHubPage'));
const ManualInvoicePage = lazy(() => import('./components/invoices/ManualInvoicePage'));
const FeedAnalysisPage = lazy(() => import('./components/invoices/FeedAnalysisPage'));
const FeedInvoicesPage = lazy(() => import('./components/feed/FeedInvoicesPage'));
const EventsPage = lazy(() => import('./components/events/EventsPage'));
const ProgramsPage = lazy(() => import('./components/programs/ProgramsPage'));
const ProgramDetailPage = lazy(() => import('./components/programs/ProgramDetailPage'));
const HorseDirectoryPage = lazy(() => import('./components/horses/HorseDirectoryPage'));
const PlanningPage = lazy(() => import('./components/planning/PlanningPage'));
const DayRatePage = lazy(() => import('./components/farm/DayRatePage'));
const MyFarmPage = lazy(() => import('./components/farm/MyFarmPage'));
const DepartureReportPage = lazy(() => import('./components/reports/DepartureReportPage'));
const EmailCenterPage = lazy(() => import('./components/emails/EmailCenterPage'));
const SaleDetails = lazy(() => import('./components/sales/SaleDetails'));
const ViewerForm = lazy(() => import('./components/sales/ViewerForm'));
const ViewerCard = lazy(() => import('./components/sales/ViewerCard'));
const SalesHub = lazy(() => import('./components/sales/SalesHub'));
const HorseAnalytics = lazy(() => import('./components/sales/HorseAnalytics'));
const ReviewCards = lazy(() => import('./components/sales/ReviewCards'));
const VetCard = lazy(() => import('./components/sales/VetCard'));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage'));
const MediaManagementPage = lazy(() => import('./components/settings/MediaManagementPage'));
const AccountPage = lazy(() => import('./components/account/AccountPage'));
const CreateAccountPage = lazy(() => import('./components/account/CreateAccountPage'));
const MyTeamPage = lazy(() => import('./components/account/MyTeamPage'));
const ClientOnboardingPage = lazy(() => import('./components/account/ClientOnboardingPage'));
const BookingRequestStub = lazy(() => import('./components/bookings/BookingRequestStub'));
const ShareLinkHandler = lazy(() => import('./context/ShareLinkHandler'));
const GuestRegisterPage = lazy(() => import('./components/account/GuestRegisterPage'));

const accountRoute = (element) => (
    <ProtectedRoute minRank={PermissionConstants.VIEW_COSTS} allowViewOnly={false}>
        {element}
    </ProtectedRoute>
);


function App() {
    return (
        <ThemeProvider>
        <AuthProvider>
            <Router>
                <Suspense fallback={<LoadingSpinner fullPage />}>
                <Routes>
                    {/* ====================== PUBLIC ROUTES ====================== */}
                    {/* Existing public routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/unauthorized" element={<UnauthorizedPage />} />

                    {/* NEW: Share link entry point */}
                    <Route path="/share/:token" element={<ShareLinkHandler />} />
                    <Route path="/guest-register" element={<GuestRegisterPage />} />
                    <Route path="/share-expired" element={<div>Share link expired or invalid</div>} />
                    {/* ====================== PROTECTED ROUTES ====================== */}

                    {/* Dashboard */}
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.MIN_RANK} allowViewOnly={true}>
                                <DashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Horse Management */}
                    <Route
                        path="/create-horse"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.ADD_HORSE} allowViewOnly={true}>
                                <CreateHorse />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/billing-wizard"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.CREATE_ACCOUNT}>
                                <BillingWizardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/my-clients"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.CREATE_ACCOUNT}>
                                <MyClientsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/create-own-horse"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.MIN_RANK} allowViewOnly={true}>
                                <CreateHorse clientMode={true} />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/client-onboarding"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.MIN_RANK} allowViewOnly={true}>
                                <ClientOnboardingPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/booking-request"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.MIN_RANK} allowViewOnly={true}>
                                <BookingRequestStub />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/bookings"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.MIN_RANK} allowViewOnly={true}>
                                <BookingRequestStub />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/edit-horse/:horse_id"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.ADD_HORSE} allowViewOnly={true}>
                                <EditHorsePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/horse-media/:horse_id"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.GET_HORSE} allowViewOnly={true}>
                                <HorseMediaPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/horse-directory"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.GET_HORSE} allowViewOnly={true}>
                                <HorseDirectoryPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Movements & Appointments */}
                    <Route
                        path="/movements"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_MOVE} allowViewOnly={true}>
                                <MovementsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/appointments"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_APPT} allowViewOnly={true} allowVet>
                                <AppointmentPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/my-appointments"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.ADVANCED_SETTINGS}>
                                <MyAppointmentsPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Medical */}
                    <Route
                        path="/vet-board"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.COMP_MED} allowVet={true}>
                                <VetBoardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/treatments"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_MED} allowViewOnly={true} allowVet>
                                <MedicalPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/medication-stock"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_MEDSTOCK} allowViewOnly={true} superBypass>
                                <DrugUsagePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/my-medications"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.ADVANCED_SETTINGS}>
                                <MyMedicationsPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Overview & Workflow */}
                    <Route
                        path="/overview"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.GET_HORSE} allowViewOnly={true} allowVet>
                                <OverviewPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/workflow"
                        element={
                            <ProtectedRoute
                                minRank={Math.max(PermissionConstants.VIEW_MED, PermissionConstants.VIEW_MOVE, PermissionConstants.VIEW_APPT)} allowViewOnly={true} allowVet>
                                <WorkflowPage />
                            </ProtectedRoute>
                        }
                    />


                    {/* Farm */}
                    <Route
                        path="/map"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_MAP} allowViewOnly={true}>
                                <FarmMapPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/farmwork"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_FARM} allowViewOnly={true}>
                                <FarmWorkPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/day-rates"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_FARM} allowViewOnly={true}>
                                <DayRatePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/my-farm"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.ADVANCED_SETTINGS}>
                                <MyFarmPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Feed & Invoices */}
                    <Route
                        path="/feed"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_FEED} allowViewOnly={true}>
                                <FeedPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/accounts" element={accountRoute(<AccountsHubPage />)} />
                    <Route path="/accounts/create-invoice" element={<ProtectedRoute minRank={CREATE_INVOICE_MAX_RANK} allowViewOnly={false}><ManualInvoicePage /></ProtectedRoute>} />
                    <Route path="/accounts/bills" element={accountRoute(<InvoicesPage />)} />
                    <Route path="/accounts/invoices" element={accountRoute(<InvoiceIncomePage />)} />
                    <Route path="/accounts/summary" element={accountRoute(<FinanceSummaryPage />)} />
                    <Route path="/accounts/income" element={accountRoute(<IncomingAnalysisPage />)} />
                    <Route path="/accounts/outgoing" element={accountRoute(<InvoiceInspectionPage />)} />
                    <Route path="/accounts/horses" element={accountRoute(<HorseCostsPage />)} />
                    <Route path="/accounts/feed" element={accountRoute(<FeedAnalysisPage />)} />
                    {ACCOUNT_LEGACY_REDIRECTS.map(({ from, to }) => (
                        <Route key={from} path={from} element={<Navigate to={to} replace />} />
                    ))}
                    <Route
                        path="/invoice-feed"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_COSTS} allowViewOnly={false}>
                                <FeedInvoicesPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Departure Report */}
                    <Route
                        path="/departure-report"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.SEND_EMAILS} allowViewOnly={false}>
                                <DepartureReportPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Programs (Breeding / Training / Rehab) */}
                    {/* No allowVet here: the per-path vet allowlist in ProtectedRoute
                        (breeding/rehab only) governs these, so /programs/training
                        is blocked at the route level, not just by the API. */}
                    <Route
                        path="/programs/:slug"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_PROGRAMS} allowViewOnly={true}>
                                <ProgramsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/programs/:slug/:id"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_PROGRAMS} allowViewOnly={true}>
                                <ProgramDetailPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Events & Media */}
                    <Route
                        path="/events"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_EVENTS} allowViewOnly={true}>
                                <EventsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/media"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.GET_HORSE} allowViewOnly={true}>
                                <MediaPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/media-upload"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.ADD_MEDIA} allowViewOnly={true}>
                                <MediaUploadPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/media-upload/:horse_id"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.ADD_MEDIA} allowViewOnly={true}>
                                <MediaUploadPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/emails"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.SEND_EMAILS} allowViewOnly={false}>
                                <EmailCenterPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/overview/:horse_id"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.GET_HORSE} allowViewOnly={true} allowVet>
                                <OverviewPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Timesheets */}
                    <Route
                        path="/timesheet"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.TIMESHEETS} allowViewOnly={true} superBypass>
                                <TimesheetPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/timesheets"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.VIEW_TIMESHEETS} allowViewOnly={true} superBypass>
                                <Timesheets />
                            </ProtectedRoute>
                        }
                    />

                    {/* Settings & Reports */}
                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.ADVANCED_SETTINGS} allowViewOnly={true}>
                                <SettingsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/media-management"
                        element={
                            <ProtectedRoute minRank={1} allowViewOnly={true} superBypass>
                                <MediaManagementPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/myaccount"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.MIN_RANK} allowViewOnly={true} allowVet>
                                <AccountPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/create-account"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.CREATE_ACCOUNT} allowViewOnly={true}>
                                <CreateAccountPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/my-team"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.CREATE_ACCOUNT} allowViewOnly={true}>
                                <MyTeamPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reports"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.ADVANCED_SETTINGS} allowViewOnly={true}>
                                <ReportsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/planning"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.ADVANCED_SETTINGS} allowViewOnly={true}>
                                <PlanningPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* ====================== SALES SECTION (all protected) ====================== */}
                    <Route
                        path="/sales/hub"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.SALES_OWNER} allowViewOnly={true}>
                                <SalesHub />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales/:saleId"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.SALES_OWNER} allowViewOnly={true}>
                                <SaleDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales/:saleId/viewer/card"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.SALES_WORKER} allowViewOnly={false}>
                                <ViewerCard />
                            </ProtectedRoute>
                        }
                    />

                    {/* Newly protected sales viewer pages – minRank 50000 */}
                    <Route
                        path="/sales/:saleId/viewer/form"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.SALES_WORKER} allowViewOnly={true}>
                                <ViewerForm />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales/:saleId/review"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.SALES_WORKER} allowViewOnly={true}>
                                <ReviewCards />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales/:saleId/analytics/horses"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.SALES_OWNER} allowViewOnly={true}>
                                <HorseAnalytics />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales/:saleId/vet-card"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.SALES_WORKER} allowViewOnly={true}>
                                <VetCard />
                            </ProtectedRoute>
                        }
                    />

                    {/* Security test */}
                    <Route
                        path="/security-test"
                        element={
                            <ProtectedRoute minRank={PermissionConstants.MIN_RANK} allowViewOnly={true}>

                                <SecurityTest />
                            </ProtectedRoute>
                        }
                    />

                    {/* ====================== FALLBACK ====================== */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
            </Router>
        </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
